// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { cloneElement, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Wraps a target element (e.g. a collapsed-sidebar icon) and, when `show` is
// true, portals `card` into document.body positioned right next to it. A
// plain `absolute` child would get clipped by the collapsed sidebar rail's
// `overflow-hidden` - portaling to body avoids that entirely, and measuring
// the real DOM rect means the arrow always lines up with the actual icon.
//
// `highlightIcon`, if given, also portals a bright white copy of the icon
// sitting exactly over the real one - meant to be paired with dimming the
// real sidebar rail's opacity elsewhere, so only this one icon "pops" while
// everything else looks dull. The portal sits outside the dimmed subtree, so
// it stays fully bright regardless of the rail's opacity. `highlightWidth`
// stretches that box to span the full sidebar width (rather than just the
// icon button's own narrower width) - pass the rail's width in px.
const TourAnchor = ({ show, card, children, highlightIcon, highlightWidth }) => {
  const anchorRef = useRef(null);
  const cardRef = useRef(null);
  const [coords, setCoords] = useState(null);
  const [box, setBox] = useState(null);

  useLayoutEffect(() => {
    if (!show) {
      setCoords(null);
      setBox(null);
      return;
    }
    const update = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Align to the icon's top edge, not its vertical center - centering pushed
      // the (taller) card above the viewport for icons sitting near the top.
      // Clamped below so a tall card doesn't overflow the bottom of the screen
      // for icons sitting near the bottom of the rail instead (e.g. Help) - the
      // card's real height isn't known yet on this first pass (cardRef is still
      // null before it's ever rendered), so this clamp only bites from the
      // second pass below onward.
      const cardHeight = cardRef.current?.offsetHeight;
      const margin = 12;
      const top = cardHeight ? Math.min(rect.top, window.innerHeight - cardHeight - margin) : rect.top;
      // Where the arrow needs to sit, in px from the CARD's own top edge, so it
      // still lines up with the icon's vertical center even once `top` above
      // has been clamped away from the icon's actual screen position.
      const arrowTop = rect.top + rect.height / 2 - 6 - top;
      setCoords({ top, left: rect.right + 22, arrowTop });
      setBox(
        highlightWidth
          ? { top: rect.top - 3, left: 4, width: highlightWidth - 8, height: rect.height + 6 }
          : { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
      );
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [show]);

  // The card's own height is only knowable once it's actually in the DOM, so
  // re-check the clamp right after it mounts/re-renders (still before paint,
  // so there's no visible jump).
  useLayoutEffect(() => {
    if (!show || !cardRef.current || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const cardHeight = cardRef.current.offsetHeight;
    const margin = 12;
    const clampedTop = Math.min(rect.top, window.innerHeight - cardHeight - margin);
    const arrowTop = rect.top + rect.height / 2 - 6 - clampedTop;
    setCoords((prev) =>
      prev && prev.top === clampedTop && prev.arrowTop === arrowTop
        ? prev
        : { ...prev, top: clampedTop, arrowTop }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, coords?.left]);

  return (
    <>
      {/* A real box (not `display: contents`) - a contents wrapper has no layout
          box at all, so getBoundingClientRect() would return all zeros and the
          card would land in the top-left corner of the viewport. */}
      <div ref={anchorRef} className="flex items-center justify-center">
        {children}
      </div>
      {show &&
        box &&
        highlightIcon &&
        createPortal(
          <div
            className="fixed z-[60] bg-general-background rounded-md shadow-[0px_1px_3px_0px_rgba(0,0,0,0.15)] flex items-center justify-center"
            style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
          >
            {highlightIcon}
          </div>,
          document.body
        )}
      {show &&
        coords &&
        createPortal(
          <div ref={cardRef} className="fixed z-[60]" style={{ top: coords.top, left: coords.left }}>
            {cloneElement(card, { arrowTop: coords.arrowTop })}
          </div>,
          document.body
        )}
    </>
  );
};

export default TourAnchor;
